import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom'
import ActivityFeed from './components/ActivityFeed'
import AddToHomeScreen from './components/AddToHomeScreen.tsx'
import { CalendarConnections } from './components/CalendarSharing'
import DemoWalkthrough from './components/DemoWalkthrough'
import RecipeDetail from './components/RecipeDetail'
import RecipeList from './components/RecipeList'
import { Header, MobileActionMenu } from './components/ui'
import UserProfile from './components/UserProfile'
import { AuthProvider } from './contexts/AuthContext'
import { UndoProvider } from './contexts/UndoContext'

// Memoize RecipeList to prevent unnecessary re-renders
const MemoizedRecipeList = memo(RecipeList)

// Separate route component to isolate the RecipeList from App re-renders
const HomeRoute = React.forwardRef(
  (
    {
      showFavorites,
      showRecipeForm,
      setShowRecipeForm,
      onAddToIngredients,
      sharedRecipeId,
    },
    ref
  ) => {
    return (
      <MemoizedRecipeList
        showOnlyFavorites={showFavorites}
        showRecipeForm={showRecipeForm}
        setShowRecipeForm={setShowRecipeForm}
        onAddToIngredients={onAddToIngredients}
        sharedRecipeId={sharedRecipeId}
        ref={ref}
      />
    )
  }
)

const App = () => {
  const [showFavorites, setShowFavorites] = useState(false)
  const [showRecipeForm, setShowRecipeForm] = useState(false)
  const [showDemoWalkthrough, setShowDemoWalkthrough] = useState(false)
  const recipeListRef = useRef(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const sharedRecipeId = searchParams.get('recipeId')

  // Handle URL parameters
  useEffect(() => {
    // When sharedRecipeId is processed and the component has mounted,
    // we keep the ID in the URL but update the history to avoid re-processing
    // if the user refreshes
    if (sharedRecipeId) {
      // Update browser history to remember this state without triggering a re-render
      const url = new URL(window.location.href)
      url.searchParams.set('recipeId', sharedRecipeId)
      window.history.replaceState({}, '', url.toString())
    }

    // Check for edit parameter
    const editRecipeId = searchParams.get('edit')
    if (editRecipeId) {
      setShowRecipeForm(true)
    }
  }, [sharedRecipeId, searchParams])

  // Check if this is the first time the user is visiting the app
  useEffect(() => {
    const hasSeenDemo = localStorage.getItem('hasSeenDemo')
    if (!hasSeenDemo) {
      // Wait a bit to ensure app is loaded before showing demo
      const timer = setTimeout(() => {
        setShowDemoWalkthrough(true)
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleCloseDemoWalkthrough = () => {
    setShowDemoWalkthrough(false)
    localStorage.setItem('hasSeenDemo', 'true')
  }

  const handleShowDemo = () => {
    setShowDemoWalkthrough(true)
  }

  const handleAddToIngredients = useCallback((recipeIds) => {
    // Get a reference to the RecipeList component and call its method
    if (recipeListRef.current && recipeIds && recipeIds.length > 0) {
      recipeListRef.current.handleAddIngredientsDirectly(recipeIds)
      
      // Scroll to ingredients panel if on desktop
      if (window.innerWidth >= 1024) {
        const ingredientsPanel = document.querySelector('.ingredients-panel')
        if (ingredientsPanel) {
          ingredientsPanel.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      }
    }
  }, [])

  return (
    <AuthProvider>
      <UndoProvider>
        <div className="min-h-screen bg-gray-50 pb-16 lg:pb-0 z-1000">
            {/* Toast container */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#FFF',
                  color: '#333',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                },
              }}
            />
            <Header title="🍽Forkast" onShowDemo={handleShowDemo} />
            <AddToHomeScreen />
            <main className="mb-4" role="main" aria-label="Main content">
              <div className="max-w-[1440px] mx-auto px-2 sm:px-3">
                <Routes>
                  <Route
                    path="/"
                    element={
                      <HomeRoute
                        showFavorites={showFavorites}
                        showRecipeForm={showRecipeForm}
                        setShowRecipeForm={setShowRecipeForm}
                        onAddToIngredients={handleAddToIngredients}
                        sharedRecipeId={sharedRecipeId}
                        ref={recipeListRef}
                      />
                    }
                  />
                  <Route path="/profile" element={<UserProfile />} />
                  <Route path="/activity" element={<ActivityFeed />} />
                  <Route
                    path="/calendar-connections"
                    element={<CalendarConnections />}
                  />
                  <Route path="/recipe/:id" element={<RecipeDetail />} />
                  {/* Catch-all route - redirect any unhandled routes to homepage */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </main>
            <MobileActionMenu
              onShowFavorites={setShowFavorites}
              currentShowFavorites={showFavorites}
              onShowRecipeForm={setShowRecipeForm}
              showRecipeForm={showRecipeForm}
              onAddToIngredients={handleAddToIngredients}
            />

            {/* Demo Walkthrough */}
            {showDemoWalkthrough && (
              <DemoWalkthrough onClose={handleCloseDemoWalkthrough} />
            )}
        </div>
      </UndoProvider>
    </AuthProvider>
  )
}

export default App
